import { getConfig } from '../config';
import { toEpochMs } from '../utils/time';

export interface PublicStats {
    publishedImageCount: number;
    latestPublishedAt: string | null;
    generatedAt: string;
}
export interface PublicImage {
    id: number;
    hash: string;
    title: string | null;
    description: string | null;
    thumbnail: string | null;
    publishedAt: string | null;
    detailUrl: string;
}
export interface RelatedRef { kind: 'image' | 'article' | 'work'; id: string; year?: string; month?: string }
export interface ObservationNote { id: string; text: string; publishedAt: string; relatedRefs: RelatedRef[]; status: 'published' }

export function publicIso(value: unknown): string | null {
    if (typeof value === 'string' && !/^\d+$/.test(value)) {
        if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/.test(value)) return null;
        const day = value.slice(0, 10);
        const midnight = new Date(`${day}T00:00:00Z`);
        if (!Number.isFinite(midnight.getTime()) || midnight.toISOString().slice(0, 10) !== day) return null;
    }
    const time = toEpochMs(value);
    return time !== null && time >= 0 && time <= 253402300799999 ? new Date(time).toISOString() : null;
}
export function publicUrl(value: unknown): string | null {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
        const url = new URL(value, window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password ? url.href : null;
    } catch { return null; }
}
export function adaptPublicStats(value: any): PublicStats {
    const generatedAt = publicIso(value?.generatedAt);
    const latestPublishedAt = publicIso(value?.latestPublishedAt);
    if (!Number.isSafeInteger(value?.publishedImageCount) || value.publishedImageCount < 0 || !generatedAt
        || (value.latestPublishedAt !== null && !latestPublishedAt) || (latestPublishedAt && latestPublishedAt > generatedAt)
        || (value.publishedImageCount === 0 && latestPublishedAt)) throw new Error('Invalid public stats');
    return { publishedImageCount: value.publishedImageCount, generatedAt, latestPublishedAt };
}
export async function fetchPublicStats(signal?: AbortSignal): Promise<PublicStats> {
    const response = await fetch(`${getConfig().apiHost.replace(/\/+$/, '')}/get_index.php?type=stats`, { signal });
    if (!response.ok) throw new Error(`Public stats HTTP ${response.status}`);
    return adaptPublicStats(await response.json());
}
export function adaptPublicImage(value: any): PublicImage | null {
    if (!Number.isSafeInteger(value?.id) || value.id < 0 || !/^[a-f0-9]{64}$/i.test(value.hash || '')
        || (value.is_discard !== undefined && value.is_discard !== 0) || value.reviewed_at === null || value.is_published === 0) return null;
    const publishedAt = publicIso(value.published_at);
    if (publishedAt && Date.parse(publishedAt) > Date.now()) return null;
    return { id: value.id, hash: value.hash,
        title: typeof value.public_title === 'string' && value.public_title.trim() ? value.public_title : null,
        description: typeof value.public_description === 'string' && value.public_description.trim() ? value.public_description : null,
        thumbnail: publicUrl(value.thumb), publishedAt,
        detailUrl: `/?page=gallery&view=image&id=${value.id}` };
}
export function parseObservations(value: unknown): ObservationNote[] {
    if (value === undefined) return [];
    if (!Array.isArray(value) || value.length > 12) throw new Error('Invalid observation records');
    const ids = new Set<string>();
    return value.map(note => {
        const publishedAt = publicIso(note?.publishedAt);
        if (!/^[a-zA-Z0-9_-]+$/.test(note?.id || '') || ids.has(note.id) || typeof note.text !== 'string' || !note.text.trim()
            || !publishedAt || !['published', 'draft'].includes(note.status) || !Array.isArray(note.relatedRefs) || note.relatedRefs.length > 12) throw new Error('Invalid observation record');
        ids.add(note.id);
        const relatedRefs = note.relatedRefs.map((ref: any): RelatedRef => {
            if (!['image', 'article', 'work'].includes(ref?.kind) || typeof ref.id !== 'string' || !/^[a-zA-Z0-9_-]+$/.test(ref.id)
                || (ref.kind === 'image' && !/^\d+$/.test(ref.id))
                || (ref.kind === 'article' && (!/^\d{4}$/.test(ref.year || '') || !/^(0[1-9]|1[0-2])$/.test(ref.month || '')))) throw new Error('Invalid observation reference');
            return ref.kind === 'article' ? { kind: ref.kind, id: ref.id, year: ref.year, month: ref.month } : { kind: ref.kind, id: ref.id };
        });
        return { id: note.id, text: note.text, publishedAt, relatedRefs, status: note.status };
    }).filter(note => note.status === 'published' && Date.parse(note.publishedAt) <= Date.now());
}
export async function publicObservations(value: unknown, isPublic: (ref: RelatedRef) => Promise<boolean>): Promise<ObservationNote[]> {
    const visible: ObservationNote[] = [];
    for (const note of parseObservations(value)) {
        let valid = true;
        for (const ref of note.relatedRefs) {
            if (!await isPublic(ref)) { valid = false; break; }
        }
        if (valid) visible.push(note);
    }
    return visible;
}
